/**
 * The customer card's SECOND TIER on the dispatcher's trip screen: which
 * number the card's lead button writes from, and — one tap away — the
 * dispatcher's own WhatsApp.
 *
 * WHY THE DEEP LINK IS DOWN HERE. Writing to a passenger AS THE OFFICE is
 * what this office does: the message goes from the company number and the
 * reply lands in the shared respond.io inbox, so whoever is on shift when
 * the passenger answers can read it. A `wa.me` link cannot do any of that —
 * it opens the sender's own WhatsApp, the passenger sees a personal mobile
 * number, and the answer arrives on one person's phone, unread by anyone
 * else and gone when they go home. So the office message is the card's green
 * button (PassengerMessage) and the personal link is in here. It is still
 * HERE, though, and not deleted: at the kerb, with one passenger who is
 * already in your chat list, opening your own WhatsApp is the right tool.
 *
 * WHICH MAKES THE COPY THE POINT. The two look identical from the outside —
 * both are "a WhatsApp to the passenger" — and a sender who confuses them
 * waits for an answer that was never coming to them. So the card says what
 * its lead button does before it is pressed, and the sheet says what the
 * other one does at the moment the choice is made. Neither is buried in a
 * tooltip.
 *
 * THE SAME IDIOM AS THE CREW'S OWN SECOND TIER (CrewSheet): one quiet,
 * block-width door on the card and the portal's own BottomSheet behind it,
 * at the thumb end of the screen. Not a new kind of drawer and not an
 * overflow menu — the portal already speaks this language in nine places,
 * and a second dialect would be worse than the button it replaced.
 *
 * It is handed to CustomerCard's `more` slot by the DISPATCHER screen only.
 * An operator and a driver fill no slots and keep the plain `wa.me` + call
 * pair they have always had, which is the whole reason the slots exist
 * rather than a flag — see Components/TripCards.
 */
import React, { useState } from 'react';
import BottomSheet from '../Components/BottomSheet';
import { WhatsAppIcon } from '../Components/Icons';
import { whatsappUrl } from '../../../Utils/contactLinks';

const FROM_THE_OFFICE = 'Message as the office goes from the company number, and the '
  + 'reply lands in the shared inbox for whoever is on shift.';

export default function PassengerMoreOptions({ trip }) {
  const [open, setOpen] = useState(false);

  const phone = trip?.customer_phone;
  // Nothing to demote and nothing to explain. The card itself shows no
  // contact controls at all until the API releases the number (24 hours
  // out), and a door onto an empty sheet is worse than no door.
  if (!phone) return null;

  const who = trip?.customer_name || 'the passenger';
  const subtitle = [trip?.booking_reference, trip?.customer_name].filter(Boolean).join(' · ');

  return (
    <>
      {/* Said where the choice is actually made — under the button it is
          about, before anybody presses it. */}
      <p className="pp-pmsg__from">{FROM_THE_OFFICE}</p>

      <button
        type="button"
        className="pp-btn pp-btn--sm pp-btn--outline pp-btn--block"
        onClick={() => setOpen(true)}
        // The accessible name CONTAINS the visible label (WCAG 2.5.3), so a
        // voice-control user says what they can read.
        aria-label={`More options for reaching ${who}`}
      >
        More options
      </button>

      {open ? (
        <BottomSheet
          title="More options"
          subtitle={subtitle}
          onClose={() => setOpen(false)}
          closeLabel="Close"
        >
          <div className="pp-pmsg__more">
            <p className="pp-sheet__text">
              {`This opens YOUR OWN WhatsApp, not the office's. ${who} will see your `
                + 'personal number, and their reply comes back to your phone — not to '
                + 'the shared inbox, where the next person on shift could answer it.'}
            </p>

            <a
              className="pp-btn pp-btn--whatsapp pp-btn--block pp-btn--contact"
              href={whatsappUrl(phone)}
              target="_blank"
              rel="noreferrer"
              // The chat opens in its own tab; the sheet has done its job.
              onClick={() => setOpen(false)}
              aria-label={`Message ${who} from your own WhatsApp`}
            >
              <WhatsAppIcon size={18} />
              Message from your own WhatsApp
            </a>

            {/* The number it will open, so nobody has to go back and read it
                off the card to be sure which chat this is. */}
            <p className="pp-sheet__note">{phone}</p>
          </div>
        </BottomSheet>
      ) : null}
    </>
  );
}
